import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Source } from '@mg/shared';
import { SourcePullStatus } from '@mg/shared';
import { RefreshCw, Rss, Tags } from 'lucide-react';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { useUserAvatar } from '../auth/UserAvatarContext';
import { cn } from '../lib/cn';
import { hcUnderlineIdle, hcUnderlineSelected } from '../lib/forcedColors';
import { useTenantKey } from '../lib/useTenantKey';
import {
  fetchFrequencyLabel,
  formatRelative,
  sourceHealthTone,
  sourceTypeLabel,
  enumLabel,
} from '../lib/display';
import { PageHeader } from '../components/shell/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { Table, TBody, TD, TH, THead, TR } from '../components/ui/Table';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/States';
import { MemberAvatar } from '../components/ui/MemberAvatar';
import { SourceFormModal } from '../features/sources/SourceFormModal';
import { DeleteSourceModal } from '../features/sources/DeleteSourceModal';
import { isOwnSource } from '../features/sources/sourceSharing';
import { SourceItemsModal } from '../features/sources/SourceItemsModal';
import { ApifyKeyWarning } from '../features/sources/ApifyKeyWarning';
import { hasBreakingApifySources } from '../features/sources/apifySources';
import { TrendTopicsPanel } from '../features/sources/TrendTopicsPanel';

type PullState = { kind: 'success'; count: number } | { kind: 'error'; message: string };

type Tab = 'sources' | 'topics';

const TABS: { id: Tab; label: string; icon: typeof Rss }[] = [
  { id: 'sources', label: 'Sources', icon: Rss },
  { id: 'topics', label: 'Trend Topics', icon: Tags },
];

/** Fully-wired CRUD against /api/sources — the end-to-end proof page. */
export function SourcesPage() {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const userId = useAuth().principal?.user.id;
  // The viewer's own picture, as the top bar shows it — an uploaded avatar or their Microsoft 365
  // photo. The member proxy behind MemberAvatar only knows about uploads.
  const { photoUrl: ownPhotoUrl } = useUserAvatar();
  const [params, setParams] = useSearchParams();
  // Deep-linkable via ?tab=topics so other pages can point straight at it.
  const tab: Tab = params.get('tab') === 'topics' ? 'topics' : 'sources';
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    if (next === 'sources') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Source | undefined>(undefined);
  const [viewingItems, setViewingItems] = useState<Source | undefined>(undefined);
  const [deleting, setDeleting] = useState<Source | undefined>(undefined);
  const [pullStatus, setPullStatus] = useState<Record<string, PullState>>({});

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['sources', tenantKey],
    queryFn: ({ signal }) => api.listSources(signal),
    enabled: tenantKey !== '',
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteSource(id),
    onSuccess: (_void, id) => {
      setDeleting(undefined);
      // Take the row out now: left up until the refetch lands, its buttons would act on a source
      // that no longer exists.
      queryClient.setQueryData<Source[]>(['sources', tenantKey], (old) =>
        old?.filter((s) => s.id !== id),
      );
      return queryClient.invalidateQueries({ queryKey: ['sources', tenantKey] });
    },
  });

  const openDelete = (source: Source) => {
    remove.reset();
    setDeleting(source);
  };

  const pull = useMutation({
    mutationFn: (id: string) => api.pullSource(id),
    onSuccess: (result, id) => {
      // The pull endpoint returns the SourcePull even when it FAILED — surface
      // the real error (e.g. an Apify "Actor was not found") instead of "0 items".
      setPullStatus((prev) => ({
        ...prev,
        [id]:
          result.status === SourcePullStatus.FAILED
            ? {
                kind: 'error',
                message: (result.error ?? 'Pull failed.').replace(/\s+/g, ' ').slice(0, 240),
              }
            : { kind: 'success', count: result.itemCount },
      }));
      void queryClient.invalidateQueries({ queryKey: ['sources', tenantKey] });
      // A successful pull regenerates the digest — refresh Trends too.
      void queryClient.invalidateQueries({ queryKey: ['brief', tenantKey] });
    },
    onError: (err, id) => {
      setPullStatus((prev) => ({
        ...prev,
        [id]: {
          kind: 'error',
          message: err instanceof ApiRequestError ? err.message : 'Pull failed',
        },
      }));
    },
  });

  const openCreate = () => {
    setEditing(undefined);
    setModalOpen(true);
  };
  const openEdit = (source: Source) => {
    setEditing(source);
    setModalOpen(true);
  };

  const sources = data ?? [];

  return (
    <>
      <PageHeader
        title="Sources"
        icon={Rss}
        description="The content sources we monitor for this workspace — RSS feeds and Apify actors."
        actions={tab === 'sources' ? <Button onClick={openCreate}>+ Add source</Button> : undefined}
      />

      <div
        role="tablist"
        aria-label="Sources views"
        className="mb-4 flex items-center gap-1 border-b border-line"
      >
        {TABS.map(({ id: tabId, label, icon: Icon }) => {
          const active = tab === tabId;
          return (
            <button
              key={tabId}
              type="button"
              role="tab"
              id={`sources-tab-${tabId}`}
              aria-selected={active}
              aria-controls={`sources-panel-${tabId}`}
              onClick={() => setTab(tabId)}
              className={cn(
                'focus-ring -mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                active
                  ? 'border-action text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink',
                active ? hcUnderlineSelected : hcUnderlineIdle,
              )}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={`sources-panel-${tab}`} aria-labelledby={`sources-tab-${tab}`}>
      {tab === 'topics' ? (
        // Keyed on the workspace: switching keeps this page mounted, and the panel's highlights,
        // chosen view and last announcement all belong to the workspace they were made in.
        <TrendTopicsPanel key={tenantKey} />
      ) : (
        // space-y, not a margin on the warning: it renders nothing while a key is set, and must not
        // leave a gap behind.
        <div className="space-y-4">
        {/* Only once an Apify source is actually failing: no key and no Apify sources is fine. */}
        {hasBreakingApifySources(sources) && <ApifyKeyWarning />}
        <Card>
        {isLoading ? (
          <LoadingState label="Loading sources…" />
        ) : isError ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load sources.'}
            onRetry={() => void refetch()}
          />
        ) : sources.length === 0 ? (
          <EmptyState
            title="No sources yet"
            description="Add your first content source to start building Daily Briefs."
            action={<Button onClick={openCreate}>+ Add source</Button>}
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Type</TH>
                <TH>Frequency</TH>
                <TH>Last pulled</TH>
                <TH className="text-right">Last pull</TH>
                <TH className="text-right">Total items</TH>
                <TH>Health</TH>
                <TH>Added by</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {sources.map((source) => {
                const status = pullStatus[source.id];
                const pulling = pull.isPending && pull.variables === source.id;
                // Pull now, Edit and Delete are the owner's alone (the API enforces it too).
                const own = isOwnSource(source, userId);
                return (
                  <TR key={source.id}>
                    <TD>
                      <div className="font-medium text-ink">{source.name}</div>
                      <div className="max-w-xs truncate text-xs text-ink-faint">
                        {source.locator}
                      </div>
                    </TD>
                    <TD>{sourceTypeLabel[source.type]}</TD>
                    <TD>{fetchFrequencyLabel[source.frequency]}</TD>
                    <TD className="text-ink-muted">{formatRelative(source.lastPulledAt)}</TD>
                    <TD className="text-right tabular-nums text-ink-muted">
                      {source.lastPullItemCount ?? '—'}
                    </TD>
                    <TD className="text-right tabular-nums text-ink-strong">
                      {source.itemCount > 0 ? (
                        <button
                          type="button"
                          onClick={() => setViewingItems(source)}
                          className="focus-ring rounded font-medium text-link hover:text-action-hover hover:underline"
                          title="View ingested items"
                        >
                          {source.itemCount}
                        </button>
                      ) : (
                        <span>0</span>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={sourceHealthTone[source.health]}>
                        {enumLabel(source.health)}
                      </Badge>
                      {source.consecutiveFailures > 0 && (
                        <span className="ml-2 text-xs text-red-500 dark:text-error-dark">
                          {source.consecutiveFailures} fails
                        </span>
                      )}
                    </TD>
                    <TD>
                      <div className="flex min-w-0 items-center gap-2">
                        {/* The name is printed beside it, so the picture is decorative. */}
                        {own ? (
                          <Avatar
                            name={source.addedBy.name}
                            size="sm"
                            fetchCustom={false}
                            photoUrl={ownPhotoUrl}
                            decorative
                          />
                        ) : (
                          <MemberAvatar person={source.addedBy} size="sm" decorative />
                        )}
                        <span className="max-w-[10rem] truncate text-ink-strong">
                          {source.addedBy.name}
                        </span>
                      </div>
                    </TD>
                    <TD className="text-right">
                      {own && (
                        <div className="flex flex-col items-end gap-1">
                          <div className="inline-flex gap-1 whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={pulling}
                              onClick={() => pull.mutate(source.id)}
                            >
                              {!pulling && <RefreshCw className="h-4 w-4" aria-hidden="true" />}
                              Pull now
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(source)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDelete(source)}
                              className="text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
                            >
                              Delete
                            </Button>
                          </div>
                          {status && !pulling && (
                            <span
                              role="status"
                              className={
                                status.kind === 'success'
                                  ? 'text-xs text-success dark:text-success-dark'
                                  : 'text-xs text-red-600 dark:text-error-dark'
                              }
                            >
                              {status.kind === 'success'
                                ? `Pulled ${status.count} item${status.count === 1 ? '' : 's'}`
                                : status.message}
                            </span>
                          )}
                        </div>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
        </Card>
        </div>
      )}
      </div>

      <SourceFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        tenantKey={tenantKey}
        source={editing}
      />

      <SourceItemsModal source={viewingItems} onClose={() => setViewingItems(undefined)} />

      <DeleteSourceModal
        key={deleting?.id ?? 'none'}
        source={deleting}
        busy={remove.isPending}
        error={
          remove.isError
            ? remove.error instanceof ApiRequestError
              ? remove.error.message
              : 'Could not delete the source.'
            : null
        }
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onClose={() => setDeleting(undefined)}
      />
    </>
  );
}
